import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import {
  type UserFileDto,
  type UserIncidentFootageMarkerDto,
  MarkerType
} from '@roadfootage/api-client'
import { fileTypeFromBlob, type FileTypeResult } from 'file-type'

import logger from '../../logger'
import { useApi } from '../../composables/api'
import { useUserStore } from '../user/user.store'
import { useIncidentSubmissionStore } from '../incident-submission'
import { processLocalVideo } from '@/utils/media-processor'
import { getExtractor } from '@/utils/vendors/registry'

// Extends base DTO for local drag-and-drop / upload tracking
export interface LocalUserFile extends UserFileDto {
  internalId: string
  file?: File
  fileTypeResult?: FileTypeResult
  isUploading: boolean
  hasError: boolean
  errorMessage?: string
  isPrimary: boolean
  markers: UserIncidentFootageMarkerDto[]
  durationSeconds?: number
  localThumbnailUrl?: string // Can be a canvas Base64 for video, or objectURL for images
  localMediaUrl?: string
}

export const useFileUploadStore = defineStore('fileUpload', () => {
  const { creatorIncidentFootageApi, creatorIncidentImagesApi } = useApi()
  const userStore = useUserStore()
  const incidentStore = useIncidentSubmissionStore()

  const isPreparing = ref(false)
  const userFileList = ref<LocalUserFile[]>([])

  // Getters
  const hasFiles = computed(() => userFileList.value.length > 0)
  const filesToUpload = computed(() =>
    userFileList.value.filter((f) => !f.isUploaded && !f.isUploading && !f.hasError)
  )
  const hasFilesToUpload = computed(() => filesToUpload.value.length > 0)
  const filesUploading = computed(() => userFileList.value.filter((f) => f.isUploading))
  const uploadedFiles = computed(() => userFileList.value.filter((f) => f.isUploaded))
  const primaryFile = computed(() => userFileList.value.find((f) => f.isPrimary))

  const isUploading = computed(() => filesUploading.value.length > 0)
  const uploadProgress = computed(() => {
    if (userFileList.value.length === 0) return 0
    return uploadedFiles.value.length / userFileList.value.length
  })

  // Keep the Incident form's primary file ID in sync with our local array
  watch(primaryFile, (newPrimary) => {
    incidentStore.incidentForm.primaryUserIncidentFileId = newPrimary?.id || null
  })

  function $reset() {
    userFileList.value = []
    isPreparing.value = false
  }

  function setPrimaryFile(internalId: string) {
    userFileList.value.forEach((f) => {
      f.isPrimary = f.internalId === internalId
    })
  }

  function updateClipRange(internalId: string, startSeconds: number, endSeconds: number) {
    const file = userFileList.value.find((f) => f.internalId === internalId)
    if (file && file.id) {
      file.markers = [
        {
          id: crypto.randomUUID(),
          userIncidentFootageId: file.id,
          offsetMs: Math.floor(startSeconds * 1000),
          markerType: MarkerType.StartOfIncident
        },
        {
          id: crypto.randomUUID(),
          userIncidentFootageId: file.id,
          offsetMs: Math.floor(endSeconds * 1000),
          markerType: MarkerType.EndOfIncident
        }
      ]
    }
  }

  function addFilesForUpload(files: File[]): LocalUserFile[] {
    const wasEmpty = userFileList.value.length === 0

    const newFiles = files.map((file) => {
      const internalId = crypto.randomUUID()
      const newFile: LocalUserFile = {
        internalId,
        file,
        uploadFileName: file.name,
        uploadMimeType: file.type,
        uploadFileSize: file.size,
        isUploaded: false,
        isUploading: false,
        hasError: false,
        isPrimary: userFileList.value.length === 0, // Default first file to primary
        markers: [],
        localMediaUrl: URL.createObjectURL(file)
      }
      userFileList.value.push(newFile)

      // Async processing for local UX
      if (file.type.startsWith('video/')) {
        processLocalVideo(file)
          .then((meta) => {
            newFile.durationSeconds = meta.duration
            newFile.localThumbnailUrl = meta.thumbnailDataUrl
          })
          .catch((e) => logger.warn(e, 'Failed to generate video thumbnail'))
      } else if (file.type.startsWith('image/')) {
        newFile.localThumbnailUrl = URL.createObjectURL(file)
      }

      return newFile
    })

    // Baseline: if no datetime was set yet and this is the first batch of files,
    // pre-populate from the first file's lastModified timestamp.
    if (wasEmpty && incidentStore.incidentForm.incidentDateTime === null && files.length > 0) {
      incidentStore.incidentForm.incidentDateTime = new Date(files[0].lastModified)
      incidentStore.incidentForm.extractedMetadataHints.dateTime = 'file'
    }

    if (wasEmpty && files.length > 0) void prefillFromFileContent(files)

    return newFiles
  }

  // Read GPS and time out of the first batch of files, in the browser, and prefill the wizard from it.
  async function prefillFromFileContent(files: File[]) {
    let meta
    try {
      meta = await getExtractor(incidentStore.incidentForm.deviceVendor).extractFromFiles?.(files)
    } catch (e) {
      // Never log file names or values.
      logger.warn(e, 'Could not read dashcam metadata from the selected files')
      return
    }
    if (meta) incidentStore.applyDashcamMetadata(meta)
  }

  function removeFile(internalId: string) {
    const index = userFileList.value.findIndex((f) => f.internalId === internalId)
    if (index > -1) {
      userFileList.value.splice(index, 1)
      if (userFileList.value.length > 0 && !userFileList.value.some((f) => f.isPrimary)) {
        setPrimaryFile(userFileList.value[0].internalId)
      }
    }
  }

  async function tryUploadFiles(): Promise<boolean> {
    if (!userStore.isProfileSetUp) throw new Error('Profile not set up.')

    // Grab files that aren't already uploading or finished
    const filesToProcess = filesToUpload.value.filter(
      (f) => !f.isUploading && !f.isUploaded && !f.hasError && f.file
    )
    if (filesToProcess.length === 0) return true

    isPreparing.value = true
    try {
      // Step 1: Hit your API to create the DB record and get the Pre-signed S3 URLs
      await Promise.all(
        filesToProcess.map(async (item) => {
          try {
            item.fileTypeResult = await fileTypeFromBlob(item.file!)
            if (!item.fileTypeResult) throw new Error('Unknown file type')

            if (item.fileTypeResult.mime.startsWith('image/')) {
              const response = await creatorIncidentImagesApi.creatorIncidentImagesCreateFile({
                userProfileId: userStore.userProfile?.id!,
                uploadUserFileRequest: item
              })
              Object.assign(item, response) // Merges the returned ID and uploadUrl into our local item
            } else if (item.fileTypeResult.mime.startsWith('video/')) {
              const response = await creatorIncidentFootageApi.creatorIncidentFootageCreateFile({
                userProfileId: userStore.userProfile?.id!,
                uploadUserFileRequest: item
              })
              Object.assign(item, response)
            }
          } catch (e: any) {
            item.hasError = true
            item.errorMessage = 'Failed to initialize upload with server.'
            logger.error(e, 'Failed to prepare file')
          }
        })
      )

      isPreparing.value = false

      // Step 2: Fire off the actual binary payloads to the S3 pre-signed URLs
      const readyToUpload = filesToProcess.filter((f) => f.uploadUrl && !f.hasError)

      // Fire and forget so the UI isn't blocked
      readyToUpload.forEach(uploadToS3)

      return true
    } catch (error) {
      logger.error({ error }, 'tryUploadFiles error')
      return false
    } finally {
      isPreparing.value = false
    }
  }

  // Helper to handle the actual PUT request to S3
  async function uploadToS3(item: LocalUserFile) {
    if (!item.file || !item.uploadUrl) return

    item.isUploading = true
    item.hasError = false

    try {
      const response = await fetch(item.uploadUrl, {
        method: 'PUT',
        body: item.file,
        headers: {
          'Content-Type': item.file.type // Ensure S3 sets the correct mime-type
        }
      })

      if (!response.ok) throw new Error(`HTTP Error: ${response.statusText}`)

      item.isUploaded = true
      item.file = undefined // Clear blob reference for memory safety
    } catch (error: any) {
      item.hasError = true
      item.errorMessage = 'Network error during upload.'
      logger.error(error, 'S3 Upload Failed')
    } finally {
      item.isUploading = false
    }
  }

  return {
    $reset,
    addFilesForUpload,
    removeFile,
    setPrimaryFile,
    updateClipRange,
    tryUploadFiles,
    userFileList,
    primaryFile,
    hasFiles,
    filesToUpload,
    uploadedFiles,
    hasFilesToUpload,
    isPreparing,
    isUploading,
    uploadProgress
  }
})
